import React, { useState, useEffect, useMemo } from 'react';
import { Footer, Header } from './headerFooter';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import ListGroup from 'react-bootstrap/ListGroup';
import { EIDIKI_TABLE_OPTIONS_D, EIDIKI_TABLE_OPTIONS } from './constants';
import { getHost } from './config';
import FilterSidebar from './FilterSidebar';
import {
    initialSidebarOpen,
    collapseFilterSidebarOnMobile,
    formatYearRangeLabel,
    joinFilterSummaryParts,
} from './filterSidebarUtils';
import 'bootstrap/dist/css/bootstrap.min.css';
import "./custom.scss";
import { useNavigate } from 'react-router-dom';

function RankingPerScore() {
    const [data, setData] = useState(null);
    const [uniqueDates, setUniqueDates] = useState([]);
    const [selectedDates, setSelectedDates] = useState([]);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [uniqueLocations, setUniqueLocations] = useState(null);
    const [yearRange, setYearRange] = useState(''); // New state for year range
    const [specialty, setSpecialty] = useState(''); // New state for specialty
    const [eidikiAgogi, setEidikiAgogi] = useState('false'); // New state for eidiki_agogi
    const [specialtyOptions, setSpecialtyOptions] = useState([]); // Options for specialty dropdown
    const [positionInTable, setPositionInTable] = useState(''); // New state for position in table input
    const [dateOptions, setDateOptions] = useState([]); // New state for fetched date options
    const [selectedValue, setSelectedValue] = useState(''); // New state for the input field value
    const [submitted, setSubmitted] = useState(false); // To check if the form was submitted
    const [selectedDate, setSelectedDate] = useState('');
    const [loading, setLoading] = useState(false); // New loading state
    const [eidikiAgogiTableType, setEidikiAgogiTableType] = useState('')
    const [sidebarOpen, setSidebarOpen] = useState(initialSidebarOpen);
    const navigate = useNavigate();
    const token = localStorage.getItem('authToken');
    

    useEffect(() => {
        if (!token) {
            navigate('/', { replace: true });
        }
    }, [token, navigate]);

    // Fetch specialties based on the selected year_range
    useEffect(() => {
        if (yearRange) { // Fetch only if yearRange is selected
            const url = `${getHost()}/api/specialty-types?year_range=${yearRange}`;
            fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            })
                .then(response => response.json())
                .then(data => setSpecialtyOptions(data.results))
                .catch(error => console.error('Error fetching specialties:', error));
        } else {
            setSpecialtyOptions([]); // Clear specialties when yearRange is not selected
        }
    }, [yearRange]);

    // Fetch dates from the /api/dates endpoint based on yearRange
    useEffect(() => {
        if (yearRange && specialty) {
            const url = `${getHost()}/api/dates?year_range=${yearRange}&specialty=${specialty}`;
            fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            })
                .then(response => response.json())
                .then(data => {
                    console.log("API Response:", data);
                    console.log("Dates is array", Array.isArray(data))
                    const sortedDates = data.sort((a, b) => new Date(a) - new Date(b));
                    setDateOptions(sortedDates || []);
                })
                .catch(error => console.error('Error fetching dates:', error));
        } else {
            setDateOptions([]);
        }
    }, [yearRange, specialty, token]);

    // Handle form submission to fetch data based on the selected filters
    const handleSubmit = () => {
        setLoading(true);
        const url = `${getHost()}/api/possible_placement`;
        const requestBody = {
            year_range: yearRange,
            specialty: specialty,
            eidiki_agogi: eidikiAgogi === 'true', // Send boolean value
            ranking: positionInTable,  // New field in request body
            phase: selectedDate,
            eidiki_agogi_table_type: eidikiAgogiTableType
        };

        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(requestBody),
        })
            .then(response => response.json())
            .then(data => {
                setData(data.results);
                setSubmitted(true);
                const dates = new Set();
                const locations = new Set();
                data.results.forEach(locationData => {
                    Object.keys(locationData).forEach(location => {
                        locations.add(location);
                        Object.keys(locationData[location]).forEach(date => {
                            dates.add(date);
                        });
                    });
                });
                setUniqueDates(Array.from(dates).sort());
                setUniqueLocations(Array.from(locations).sort());
                collapseFilterSidebarOnMobile(setSidebarOpen);
            })
            .catch(error => console.error('Error fetching data:', error))
            .finally(() => {
                setLoading(false); // Hide loading indicator regardless of success/failure
            });
    };

    const handleValueChange = (event) => {
        setSelectedValue(event.target.value);
    };

    const handlePositionChange = (event) => {
        const rawValue = event.target.value;
        const value = rawValue.trimEnd();
        if (/^\d*$/.test(value)) { // Validate that only numbers are allowed
            setPositionInTable(value);
        }
    };

    const handleDateChange = (event) => {
        setSelectedDate(event.target.value);
    };

    const handleLogout = () => {
        // Clear the authToken from localStorage
        localStorage.removeItem('authToken');
        localStorage.setItem('logout-event', Date.now());

        // Redirect to the login page
        navigate('/', { replace: true });
    }

    const isFormValid = yearRange !== '' && specialty !== '' && positionInTable !== '' && selectedDate !=='' && (eidikiAgogi === "false" || (eidikiAgogi === "true" && eidikiAgogiTableType));

    const filterSummary = useMemo(
        () => joinFilterSummaryParts([
            formatYearRangeLabel(yearRange),
            specialty,
            positionInTable ? `θέση ${positionInTable}` : '',
            selectedDate,
        ]),
        [yearRange, specialty, positionInTable, selectedDate]
    );

    return (
        <div id="root">
            <Header handleLogout={handleLogout}/>
            <main className='main-container ranking-per-state-page'>
                <FilterSidebar
                    sidebarOpen={sidebarOpen}
                    setSidebarOpen={setSidebarOpen}
                    filterSummary={filterSummary}
                    bodyId="ranking-per-score-sidebar-body"
                >
                        <Form>
                    <Form.Group className="mb-3" controlId="yearSelect.range">
                        <Form.Label className='custom-label'>Έτος:</Form.Label>
                        <Form.Select 
                            className="custom-form" 
                            value={yearRange} 
                            onChange={(e) => {
                                setYearRange(e.target.value);
                                setSpecialty("");
                                setSelectedDate("");
                                setData("");
                            }}>
                            <option>Επιλέξτε...</option>
                            <option value="2022_2023">2022-2023</option>
                            <option value="2023_2024">2023-2024</option>
                            <option value="2024_2025">2024-2025</option>
                            <option value="2025_2026">2025-2026</option>
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="yearSelect.specialty">
                        <Form.Label className='custom-label'>Ειδικότητα:</Form.Label>
                        <Form.Select 
                            className="custom-form" 
                            value={specialty} 
                            onChange={(e) => {
                                setSpecialty(e.target.value);
                                setSelectedDate("");
                                setData("");
                            }} 
                            disabled={!yearRange}>
                        <option value="">Επιλέξτε...</option>
                            {specialtyOptions.length > 0 ? (
                                specialtyOptions
                                .slice() // Create a shallow copy of the array to avoid mutating the original
                                .sort((a, b) => a.localeCompare(b)) // Sort alphabetically
                                .map((spec) => (
                                    <option key={spec} value={spec}>
                                        {spec}
                                    </option>
                                ))
                            ) : (
                                <option value="">No specialties available</option>
                            )}
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="yearSelect.position">
                        <Form.Label className='custom-label'>Θέση πίνακα:</Form.Label>
                        <Form.Control className="custom-form" type="text" placeholder="Εισάγετε τη θέση σας στον πίνακα" value={positionInTable} onChange={handlePositionChange}/>
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="yearSelect.date">
                        <Form.Label className='custom-label'>Ημερομηνία φάσης προσλήψεων:</Form.Label>
                        <Form.Select className="custom-form" onChange={handleDateChange} value={selectedDate}>
                            <option value="">Επιλέξτε...</option>
                            {dateOptions.length > 0 ? (
                                dateOptions.map(date => (
                                    <option key={date} value={date}>{date}</option>
                                ))
                            ) : (
                                <option value="">No dates available</option> // Handle empty dates case
                            )}
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="yearSelect.eidiki">
                        <Form.Label className='custom-label'>Ειδική Αγωγή:</Form.Label>
                        <Form.Select 
                            className="custom-form" 
                            value={eidikiAgogi} 
                            onChange={(e) => {
                                const value = e.target.value;
                                setEidikiAgogi(value);
                                setData("");
                                if (value === "false") {
                                    setEidikiAgogiTableType(EIDIKI_TABLE_OPTIONS[0]?.value || "");
                                }
                            }}>
                            <option value="false">Όχι</option>
                            <option value="true">Ναι</option>
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label className='custom-label'>Είδος πίνακα:</Form.Label>
                        <Form.Select 
                            className="custom-form" 
                            name="table_value" 
                            value={eidikiAgogiTableType} 
                            onChange={(e) => { 
                                setEidikiAgogiTableType(e.target.value);
                                setData("");
                            }} 
                            required
                            disabled={eidikiAgogi === "false"}
                            >
                            <option value="">Επιλέξτε πίνακα</option>
                            {EIDIKI_TABLE_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option> ))}
                        </Form.Select>
                    </Form.Group>
                                </Form>
                                <p className="ranking-per-state-sidebar-note text-muted fst-italic small">
                                    * Η επιλογή του είδους πίνακα αφορά την ειδική αγωγή.<br/>
                                    - Αν επιλέξετε <b>Ειδική αγωγή: Όχι</b>, η θέση που καταχωρείτε προέρχεται από τον πίνακα γενικής αγωγής.<br/>
                                    - Αν επιλέξετε <b>Ειδική αγωγή: Ναι</b>, η θέση που καταχωρείτε μπορεί να προέρχεται από τον:<br/>
                                    <ul>
                                        <li><b>Κύριο</b> πίνακα ειδικής αγωγής (απόφοιτοι ΑΕΙ στην ΕΑΕ, κάτοχοι διδακτορικών & μεταπτυχιακών στην ΕΑΕ)</li>
                                        <li><b>Επικουρικό</b> πίνακα ειδικής αγωγής (σεμινάριο/ εξειδίκευση στην ΕΑΕ 400 ωρών, προϋπηρεσία τουλάχιστον 10 μηνών στην ΕΑΕ)</li>
                                        <li>πίνακα <b>Γενικής</b> αγωγής (λοιποί, κατόπιν εξάντλησης των παραπάνω)</li>
                                    </ul>
                                </p>
                                <p className="ranking-per-state-sidebar-note text-muted fst-italic small">
                                    ** <strong>Διευκρίνιση: </strong>Ο εκπαιδευτικός θα πρέπει να εισάγει τη θέση βάσει του αρχικού πίνακα και όχι των εκκαθαρισμένων.
                                </p>
                                <div className="d-flex justify-content-center mb-4">
                                    <Button
                                        variant="secondary"
                                        type="button"
                                        className="predict-submit-button"
                                        onClick={handleSubmit}
                                        disabled={!isFormValid}
                                    >
                                        Υποβολή
                                    </Button>
                                </div>
                </FilterSidebar>
                <div className={`ranking-per-state-main ${!sidebarOpen ? 'ranking-per-state-main--sidebar-collapsed' : ''}`}>
                    <Container fluid className="ranking-per-state-container">
                        <Row className="g-3 g-md-4">
                            <Col xs={12}>
                                <div className="ranking-per-state-content">
                                {!submitted && !data && !loading && (
                                    <p className="text-muted small mb-2">
                                        Ο εκπαιδευτικός εισάγει τη θέση του βάσει του αρχικού πίνακα (όχι του εκκαθαρισμένου),
                                        τη σχολική χρονιά που τον ενδιαφέρει, την ειδικότητά του και τη φάση προσλήψεων που τον ενδιαφέρει.
                                        Το αποτέλεσμα που προκύπτει είναι όλες οι περιοχές που θα μπορούσε να είχε προσληφθεί
                                        τη συγκεκριμένη σχολική χρονιά και φάση προσλήψεων βάσει της θέσης του.<br />
                                        <br /><strong>Παράδειγμα αποτελέσματος: </strong><br />
                                        Έστω ότι ο εκπαιδευτικός εισάγει: 2023-2024, ΠΕ70, 1500, 2023-09-01<br />
                                        Το αποτέλεσμα που προκύπτει είναι το παρακάτω:
                                        <ul>
                                            <li>ΦΛΩΡΙΝΑΣ (Π.Ε.)</li>
                                            <li>Πρώτος στον πίνακα: 529</li>
                                            <li>Τελευταίος στον πίνακα: 1619</li>
                                            <li>Συνολικές προσλήψεις: 3</li>
                                        </ul>
                                        Άρα ο εκπαιδευτικός με θέση 1500 θα μπορούσε να έχει προσληφθεί στη Φλώρινα καθώς ο τελευταίος στη λίστα των προσλήψεων είχε θέση 1619.<br /><br />
                                        <strong>Διευκρίνιση: </strong>Ο εκπαιδευτικός θα πρέπει να εισάγει τη θέση βάσει του αρχικού πίνακα και όχι των εκκαθαρισμένων.
                                    </p>
                                )}
                                {loading && (
                                    <div className="text-center mt-3 mb-3">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <div>Φόρτωση αποτελεσμάτων...</div>
                                    </div>
                                )}

                                {submitted && data ? (
                                    <div className={!sidebarOpen ? 'results-snug-mobile' : ''}>
                                        <div className="data-container">
                            {data.length > 0 ? (
                                data.map((locationData2, index) => (
                                    <div>
                                        {Object.entries(locationData2).map(([location, info]) => (
                                            <Card className='custom-card' key={Object.keys(locationData2)[0]}>
                                                <Card.Body>
                                                    <Card.Title>{location}</Card.Title>
                                                    <ListGroup className="list-group-flush">
                                                    <ListGroup.Item>
                                                        Σειρά Πρώτου: {info.table_min}
                                                        {eidikiAgogi === "true" && info.table_min_type !== null && ` (${EIDIKI_TABLE_OPTIONS_D[info.table_min_type]})`}
                                                    </ListGroup.Item>
                                                    {info.table_min_score !== null && <ListGroup.Item>
                                                        Μόρια Πρώτου: {info.table_min_score} 
                                                    </ListGroup.Item>}
                                                    <ListGroup.Item>
                                                        Σειρά Τελευταίου: {info.table_max}
                                                        {eidikiAgogi === "true" && info.table_max_type !== null && ` (${EIDIKI_TABLE_OPTIONS_D[info.table_max_type]})`}
                                                    </ListGroup.Item>
                                                    {info.table_max_score !== null && <ListGroup.Item>
                                                        Μόρια Τελευταίου: {info.table_max_score} 
                                                    </ListGroup.Item>}
                                                    <ListGroup.Item>Συνολικές προσλήψεις: {info.total}</ListGroup.Item>
                                                </ListGroup> 
                                                </Card.Body>
                                            </Card>
                                        ))}
                                    </div>
                                ))
                            ) : (
                                <Card className='custom-card'>
                                    <Card.Body>
                                        <Card.Title>Δεν υπάρχουν αποτελέσματα</Card.Title>
                                    </Card.Body>
                                </Card> 
                            )}
                                        </div>
                                    </div>
                                ) : !loading && null}
                                </div>
                            </Col>
                        </Row>
                    </Container>
                </div>
            </main>
            <Footer />
        </div>
    );
}

export default RankingPerScore;
